import { useTranslation } from "react-i18next";
import { Navbar } from "react-bootstrap";
import { Dropdown } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import SettingsForm from "@serie3/mymediaplanner/Modules/User/Forms/SettingsForm";
import { useFormik } from "formik";
import i18n from "../../i18n";
import { useContext } from "react";
import SettingsContext from "@serie3/common/auth/SettingsContext";
import { Roles } from "@serie3/common/config/RoleRights";
import { ProfileConfig } from "@serie3/common/Modules/Profile/Resources/Endpoints";
import EllipsisVerticalToggle from "../shared/Dropdown/EllipsisVerticalToggle";
import {
  faCog,
  faRightFromBracket,
  faUser,
  faBars,
  faIdCard,
  faLink,
  faGears,
} from "@fortawesome/free-solid-svg-icons";
import { useEffect, useState } from "react";
import InvitationConfig from "@serie3/common/Modules/Invitations/Resources/Endpoints";
import GroupConfig from "@serie3/common/Modules/Groups/Resources/Endpoints";
import crudApi from "@serie3/common/api/crudApi";
import { getAuth, signOut } from "firebase/auth";
import firebaseConfig from "@serie3/mymediaplanner/Config/Firebase/firebase";
import useToken from "../../security/useToken";
import { initializeApp } from "firebase/app";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import SetupAssistant from "./components/SetupAssistant/SetupAssistant";
import CustomToggleBtn from "../shared/Dropdown/CustomToggleBtn";
import IconBubble from "../shared/Dropdown/IconBubble";
import GermanFlag from "../../assets/images/flags/germany.jpg";
import UsFlag from "../../assets/images/flags/us.jpg";

const Header = ({
  showSidebar,
  setShowSidebar,
  currentLanguage,
  setCurrentLanguage,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { settings } = useContext(SettingsContext);

  const [invitations, setInvitations] = useState([]);
  const [quicklinkGroups, setQuicklinkGroups] = useState([]);
  const [showAssistant, setShowAssistant] = useState(false);
  const [slideIndex, setSlideIndex] = useState(0);
  const [isFirstLogin, setIsFirstLogin] = useState(false);
  const [languageFlag, setLanguageFlag] = useState(null);
  const [systemProfile, setSystemProfile] = useState(null);

  const userRoles = settings?.user?.roles
    ? Object.values(settings.user.roles)
    : (() => { try { return JSON.parse(localStorage.getItem("roles") ?? "[]"); } catch { return []; } })();

  const primaryRole = (() => {
    if (userRoles.includes(Roles.SUPER_ADMIN)) return { label: "Super Admin",  color: "#7c3aed" };
    if (userRoles.includes(Roles.ADMIN))       return { label: "Administrator", color: "#0369a1" };
    if (userRoles.includes(Roles.EDITOR))      return { label: "Editor",        color: "#0891b2" };
    if (userRoles.includes(Roles.VIEWER))      return { label: "Viewer",        color: "#6b7280" };
    return { label: "User", color: "#6b7280" };
  })();

  const displayName = (() => {
    const vorname = systemProfile?.vorname?.trim();
    const name = systemProfile?.name?.trim();
    if (vorname || name) return [vorname, name].filter(Boolean).join(" ");
    return settings?.user?.username ?? settings?.email ?? "—";
  })();

  const initials = displayName
    .trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? "").join("");

  const editSettingsForm = SettingsForm.createSettingsForm(i18n);
  const formik = useFormik(editSettingsForm);

  useEffect(() => {
    setIsFirstLogin(!!settings?.is_first_login);
    setLanguageFlag(settings?.language);
  }, [settings]);

  useEffect(() => {
    // run once when settings arrive
    if (isFirstLogin) {
      setShowAssistant(true);
      setSlideIndex(0); // start at first slide if you want
    }
  }, [isFirstLogin]);

  const changeLanguage = (lng = "en") => {
    formik.setFieldValue("id", settings.id);
    formik.setFieldValue("language", lng);
    formik.submitForm();
    i18n.changeLanguage(lng);
    setCurrentLanguage(i18n.language);
    setLanguageFlag(lng);
  };

  useEffect(() => {
    const getInvitations = async () => {
      await crudApi.getAll(
        InvitationConfig.endpoints.listOwnInvitations,
        setInvitations,
        InvitationConfig.datafield
      );
    };
    const getSystemProfile = async () => {
      const response = await crudApi.justGet(ProfileConfig.endpoints.systemProfile);
      if (response.ok) setSystemProfile(response.data);
    };
    const getQuicklinks = async () => {
      const response = await crudApi.justGet(GroupConfig.endpoints.quicklinks);
      if (response?.ok) {
        const list = Array.isArray(response.data) ? response.data : response.data?.items ?? [];
        setQuicklinkGroups(list);
      }
    };
    getInvitations();
    getSystemProfile();
    getQuicklinks();
  }, []);

  const app = initializeApp(firebaseConfig);
  const auth = getAuth(app);
  const { setToken } = useToken();

  return (
    <>
      <SetupAssistant
        show={showAssistant}
        setShow={setShowAssistant}
        slideIndex={slideIndex}
        setSlideIndex={setSlideIndex}
        isFirstLogin={isFirstLogin}
        setIsFirstLogin={setIsFirstLogin}
      />

      <header id="page-topbar">
        <div className="navbar-header">

          {/* Left: hamburger + chrome plugin */}
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="topbar-icon-btn"
              id="vertical-menu-btn"
              onClick={() => setShowSidebar(!showSidebar)}
            >
              <FontAwesomeIcon icon={faBars} />
            </button>

            <a
              href="https://mymediaplanner.com/fileadmin/user_upload/mymediaplanner/linkedin.zip"
              className="topbar-chip"
            >
              <i className="uil uil-dropbox" style={{ fontSize: 15 }} />
              <span>Chrome Plugin</span>
            </a>
          </div>

          {/* Right: actions */}
          <div className="d-flex align-items-center gap-4" style={{ paddingRight: 24 }}>

            {/* Setup guide */}
            <button
              type="button"
              className="topbar-icon-btn topbar-icon-btn--bubble"
              title="Setup guide"
              onClick={(e) => { e.preventDefault(); setSlideIndex(0); setShowAssistant(true); }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
                fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
              </svg>
            </button>

            {/* Notifications */}
            <button
              type="button"
              className="topbar-icon-btn topbar-icon-btn--bubble"
              style={{ position: "relative" }}
              title="Invitations"
              onClick={(e) => { e.preventDefault(); navigate("/invitations/"); }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
                fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
              {invitations.length > 0 && (
                <span style={{
                  position: "absolute", top: 5, right: 5,
                  width: 8, height: 8, borderRadius: "50%",
                  background: "#f34e4e", border: "2px solid #fff",
                }} />
              )}
            </button>

            {/* Language */}
            <Dropdown>
              <Dropdown.Toggle as={CustomToggleBtn}>
                {languageFlag === "de"
                  ? <img src={GermanFlag} alt="DE" height={18} style={{ borderRadius: 3 }} />
                  : <img src={UsFlag} alt="EN" height={18} style={{ borderRadius: 3 }} />}
              </Dropdown.Toggle>
              <Dropdown.Menu align="end">
                <Dropdown.Item onClick={() => changeLanguage("de")} className="d-flex align-items-center gap-2">
                  <img src={GermanFlag} alt="DE" height={14} style={{ borderRadius: 2 }} />
                  {t("Translation.German")}
                </Dropdown.Item>
                <Dropdown.Item onClick={() => changeLanguage("en")} className="d-flex align-items-center gap-2">
                  <img src={UsFlag} alt="EN" height={14} style={{ borderRadius: 2 }} />
                  {t("Translation.English")}
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>

            {/* Quicklinks */}
            {quicklinkGroups.length > 0 && (
              <Dropdown>
                <Dropdown.Toggle as={CustomToggleBtn}>
                  <FontAwesomeIcon icon={faLink} style={{ fontSize: 16 }} />
                </Dropdown.Toggle>
                <Dropdown.Menu align="end">
                  {quicklinkGroups.map((group) => (
                    <Dropdown.Item
                      key={group.id}
                      onClick={() => navigate(`/groups/${group.id}`)}
                    >
                      {group.name}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            )}

            {/* User menu */}
            <Dropdown>
              <Dropdown.Toggle
                as="button"
                style={{
                  display: "inline-flex", alignItems: "center", gap: 10,
                  background: "transparent", border: "none", cursor: "pointer",
                  padding: "4px 8px", borderRadius: 10,
                  transition: "background 0.15s ease",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(3,142,220,0.08)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
              >
                <div style={{
                  width: 34, height: 34, borderRadius: "50%", flexShrink: 0,
                  background: "linear-gradient(135deg, #564ab1, #3d3490)",
                  color: "#fff", fontSize: 12, fontWeight: 700,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  letterSpacing: 0.5,
                }}>
                  {initials || <FontAwesomeIcon icon={faUser} style={{ fontSize: 13 }} />}
                </div>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", lineHeight: 1.3 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "#111827", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {displayName}
                  </span>
                  <span style={{ fontSize: 11, color: "#6b7280", fontWeight: 400 }}>
                    {primaryRole.label}
                  </span>
                </div>
              </Dropdown.Toggle>
              <Dropdown.Menu align="end">
                <Dropdown.Item onClick={() => navigate("/account")} className="d-flex align-items-center">
                  <IconBubble icon={faIdCard} bg="rgba(3,142,220,0.12)" color="#038edc" />
                  {t("Backend.Settings.Tabs.Account.Title", "Account")}
                </Dropdown.Item>
                <Dropdown.Item onClick={() => navigate("/settings")} className="d-flex align-items-center">
                  <IconBubble icon={faCog} bg="rgba(100,116,139,0.12)" color="#64748b" />
                  {t("Backend.Header.Menu.Settings")}
                </Dropdown.Item>
                {userRoles.includes(Roles.SUPER_ADMIN) && (
                  <>
                    <div className="dropdown-divider" />
                    <Dropdown.Item onClick={() => navigate("/admin")} className="d-flex align-items-center">
                      <IconBubble icon={faGears} bg="rgba(124,58,237,0.12)" color="#7c3aed" />
                      Admin
                    </Dropdown.Item>
                  </>
                )}
                <div className="dropdown-divider" />
                <Dropdown.Item
                  className="d-flex align-items-center"
                  onClick={() => { setToken(null, null); signOut(auth); navigate("/logout"); }}
                >
                  <IconBubble icon={faRightFromBracket} bg="rgba(243,78,78,0.12)" color="#f34e4e" />
                  <span style={{ color: "#f34e4e" }}>{t("Backend.Header.Menu.Logout")}</span>
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>

          </div>
        </div>
      </header>
    </>
  );
};

export default Header;
